import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import Loader from "../../components/loader/loader";
import FloatingLabelInput from "../FloatingLabelInput/FloatingLabelInput";
import { UPDATE_PACKAGE_MUTATION } from "../../graphql/mutations/updatePackageMutation";
import { toastFunction, errorToastFunction } from "../../utils/toast";

const EditPackage = ({ onCancel, packageData }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm({ defaultValues: {} });

  const [updatePackage, { loading }] = useMutation(UPDATE_PACKAGE_MUTATION);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (packageData) {
      reset({
        name: packageData.name,
        price: packageData.price,
        limit_dailywh: packageData.limitDailywh,
        limit_mamps: packageData.limitMamps,
        reset_time: packageData.resetTime,
      });
    }
  }, [packageData, reset]);

  const onSubmit = async (data) => {
    const updatedData = {
      name: data.name,
      price: parseFloat(data.price),
      limit_mamps: parseFloat(data.limit_mamps),
      limit_dailywh: parseFloat(data.limit_dailywh),
      reset_time: parseFloat(data.reset_time),
    };

    try {
      setErrorMessage("");
      const { data: res } = await updatePackage({
        variables: {
          id: parseInt(packageData.id, 10),
          packageData: updatedData,
        },
      });

      toastFunction(
        `Successfully updated package ${res.updatePackage.package.name}`,
        "success",
        "package-update-success"
      );
      onCancel();
    } catch (error) {
      console.error("Update package error:", error);

      let displayError = "Failed to update package. Please try again.";

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        displayError = error.graphQLErrors[0].message;
      } else if (error.networkError) {
        displayError = `Network error: ${error.networkError.message}`;
      } else if (error.message) {
        displayError = error.message;
      }

      setErrorMessage(displayError);
      errorToastFunction(displayError, "package-update-error");
    }
  };

  if (loading) return <Loader sizeClass={"size--medium"} />;

  return (
    <div className="basic-modal">
      <div className="basic-modal__header">
        <h3>Edit Package</h3>
      </div>
      <div className="basic-modal__body">
        {errorMessage && (
          <div
            className="input-container__error"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
              fontSize: "14px",
              lineHeight: "1.5",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {errorMessage}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="basic-modal__form">
          <FloatingLabelInput
            type="text"
            name="name"
            register={register}
            rules={{ required: "The name field is required." }}
            placeholder="Name"
            errors={errors}
          />
          <FloatingLabelInput
            type="number"
            name="price"
            register={register}
            rules={{ required: "The price field is required." }}
            placeholder="Price"
            errors={errors}
          />
          <FloatingLabelInput
            type="number"
            name="limit_dailywh"
            register={register}
            rules={{ required: "The Daily Limit field is required." }}
            placeholder="Daily Limit"
            errors={errors}
          />
          <FloatingLabelInput
            type="number"
            name="limit_mamps"
            register={register}
            rules={{ required: "The Current Limit field is required." }}
            placeholder="Current Limit"
            errors={errors}
          />
          <FloatingLabelInput
            type="number"
            name="reset_time"
            register={register}
            rules={{ required: "The Reset Time field is required." }}
            placeholder="Reset Time"
            errors={errors}
          />
          <div className="basic-modal__footer">
            <button onClick={onCancel} className="button button--secondary">
              Cancel
            </button>
            <button type="submit" className="button button--primary">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPackage;
